import { useEffect, useState } from 'react';
import { BookingStep, WidgetConfiguration } from './booking-types';
import useStepBuilder from './useStepBuilder';

/**
 * Custom hook to manage booking steps
 */
export const useBookingSteps = (widgetConfiguration: WidgetConfiguration | null, bookingPageConfig: any) => {
  // Initialize current step based on configuration
  const initialStep = widgetConfiguration?.isLocationsActivated
    ? BookingStep.LOCATION_STEP
    : BookingStep.SERVICE_SELECTION;

  const [currentStep, setCurrentStep] = useState<BookingStep>(initialStep);

  // Use the step builder hook to generate steps
  const { steps: generatedSteps, isLoading: stepsLoading } = useStepBuilder(widgetConfiguration, bookingPageConfig);

  // Update step when configuration changes
  useEffect(() => {
    if (widgetConfiguration) {
      const newInitialStep = widgetConfiguration.isLocationsActivated
        ? BookingStep.LOCATION_STEP
        : BookingStep.SERVICE_SELECTION;

      setCurrentStep(newInitialStep);
    }
  }, [widgetConfiguration]);

  // Find next step based on step order in generated steps
  const findNextStep = (): BookingStep | null => {
    const currentIndex = generatedSteps.findIndex(s => s.step === currentStep);
    if (currentIndex === -1 || currentIndex >= generatedSteps.length - 1) {
      return null; // No next step
    }
    return generatedSteps[currentIndex + 1].step;
  };

  // Find previous step based on step order in generated steps
  const findPrevStep = (): BookingStep | null => {
    const currentIndex = generatedSteps.findIndex(s => s.step === currentStep);
    if (currentIndex <= 0) {
      return null; // No previous step
    }
    return generatedSteps[currentIndex - 1].step;
  };

  // Go to next step based on configuration
  const nextStep = () => {
    const next = findNextStep();
    if (next !== null) {
      setCurrentStep(next);
    }
  };

  // Go to previous step based on configuration
  const prevStep = () => {
    const prev = findPrevStep();
    if (prev !== null) {
      setCurrentStep(prev);
    }
  };

  const setStep = (step: BookingStep) => {
    setCurrentStep(step);
  };
  return {
    currentStep,
    steps: generatedSteps,
    stepsLoading,
    nextStep,
    prevStep,
    setStep,
    findNextStep,
    findPrevStep
  };
};

export default useBookingSteps;
